import React from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useUI } from 'src/hooks/use-ui';
import useHasMobileApp from 'src/utils/use-has-mobile-app';

type MobileSettingsMovedBannerProps = {
  /** Which enterprise Mobile App tab this dispensary's mobile settings now live under. */
  tabSegment: 'navigation' | 'product-cards' | 'theme';
};

/**
 * Points anyone still looking for mobile-app settings on the (now desktop/shared-only, see
 * ThemeFormProps.mobileOnly etc.) per-dispensary Theme / Product Cards / Navigation pages at
 * their new home: this dispensary's Theme & Branding / Product Cards / Navigation tab under the
 * enterprise's consolidated Mobile App tab.
 *
 * Only renders when there's actually somewhere to send them: the enterprise Mobile App tab
 * exists (`ecomm.app.advanced-customization`), this dispensary belongs to an enterprise
 * (`retailer.enterpriseId`), and it has the mobile app enabled. If any of those is missing,
 * `/enterprise/:enterpriseId/mobile-app/...` wouldn't resolve to anything useful for this
 * dispensary anyway.
 */
export function MobileSettingsMovedBanner({ tabSegment }: MobileSettingsMovedBannerProps): JSX.Element | null {
  const { id: dispensaryId } = useParams<{ id: string }>();
  const flags = useFlags();
  const UI = useUI();
  const hasMobileApp = useHasMobileApp();
  const enterpriseId = UI.dispensary.retailer?.enterpriseId;

  if (!(flags['ecomm.app.advanced-customization'] ?? false) || !enterpriseId || !hasMobileApp) {
    return null;
  }

  return (
    <Banner>
      Looking for mobile settings?{' '}
      <StyledLink to={`/enterprise/${enterpriseId}/mobile-app/${tabSegment}/${dispensaryId}`}>They moved!</StyledLink>
    </Banner>
  );
}

const Banner = styled.div`
  background: #eef6fd;
  border: 1px solid #cfe4f6;
  border-radius: 6px;
  color: #2c3236;
  font-size: 13px;
  margin-bottom: 20px;
  padding: 10px 14px;
`;

const StyledLink = styled(Link)`
  color: #0b99e6;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
